// VerifyEmailSentPage — landing after BE-8 signup.
//
// Per Wave 4 spec: signup returns 202 with no session cookie. The user
// must verify their email before logging in. This page tells them to
// check their inbox, with a Resend button (BE-22) for when the
// verification email never arrives.

import { Button, buttonVariants, Card, IntelliAuthLogo } from '@intelliauth/ui'
import { CheckCircle2, Mail, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { CpV2ApiError, cpClient } from '@/lib/api/cpClient'

const RESEND_COOLDOWN_S = 60

export default function VerifyEmailSentPage() {
  const [params] = useSearchParams()
  const email = params.get('email') ?? ''

  const [resending, setResending] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const [resentOnce, setResentOnce] = useState(false)

  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  const handleResend = async () => {
    if (!email || cooldown > 0) return
    setResending(true)
    try {
      await cpClient().post('/api/v1/auth/verify/request', {
        body: { email },
      })
      toast.success('Verification email resent.')
      setResentOnce(true)
      setCooldown(RESEND_COOLDOWN_S)
    } catch (err) {
      const msg =
        err instanceof CpV2ApiError ? err.message : err instanceof Error ? err.message : 'Failed'
      toast.error(msg)
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-[440px]">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center">
            <IntelliAuthLogo size="md" />
          </Link>
        </div>

        <Card className="p-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-5">
            <Mail className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground text-center mb-2">
            Check your email
          </h1>
          <p className="text-sm text-muted-foreground text-center mb-6">
            {email ? (
              <>
                We sent a verification link to <strong className="text-foreground">{email}</strong>.
                Click the link to activate your account.
              </>
            ) : (
              <>We sent you a verification link. Click it to activate your account.</>
            )}
          </p>

          {resentOnce && (
            <div className="flex items-center gap-2 text-xs text-emerald-500 mb-4 justify-center">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Verification email resent.
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={handleResend}
            disabled={!email || cooldown > 0}
            loading={resending}
            leftIcon={RefreshCw}
            className="w-full"
          >
            {cooldown > 0
              ? `Resend in ${cooldown}s`
              : resentOnce
                ? 'Resend again'
                : 'Resend verification email'}
          </Button>

          <div className="mt-6 text-center">
            <p className="text-xs text-muted-foreground/70">
              Didn't get the email? Check spam, or{' '}
              <Link to="/login" className="text-primary hover:text-primary/80 font-medium">
                go to sign in
              </Link>{' '}
              if you've already verified.
            </p>
          </div>
        </Card>

        <div className="mt-6 text-center">
          <Link to="/login" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  )
}
